"use client";

import type { QueryBlogIndexPageDataResult } from "@/lib/sanity/sanity.types";
import Image from "next/image";
import Link from "next/link";

import { SanityImage } from "./elements/sanity-image";

type Blog = NonNullable<
  NonNullable<QueryBlogIndexPageDataResult>["blogs"]
>[number];

type BlogImageProps = {
  image: Blog["image"];
  title?: string | null;
};

function BlogImage({ image, title }: BlogImageProps) {
  if (!image?.id) {
    return (
      <div className="aspect-[4/3] w-full bg-gradient-to-br from-gray-100 to-gray-200" />
    );
  }

  return (
    <SanityImage
      alt={title ?? "Blog post image"}
      className="aspect-[4/3] w-full object-cover transition-transform duration-300 group-hover:scale-105"
      height={400}
      image={image}
      width={600}
    />
  );
}

type BlogCardProps = {
  blog: Blog;
};

export function BlogCard({ blog }: BlogCardProps) {
  if (!blog) {
    return (
      <article className="overflow-hidden bg-white">
        <div className="h-64 animate-pulse bg-gradient-to-br from-gray-200 to-gray-300" />
        <div className="space-y-3 py-6">
          <div className="h-4 w-32 animate-pulse rounded bg-gray-200" />
          <div className="h-6 w-full animate-pulse rounded bg-gray-200" />
          <div className="h-4 w-24 animate-pulse rounded bg-gray-200" />
        </div>
      </article>
    );
  }

  const { title, publishedAt, slug, image } = blog;

  return (
    <Link href={slug ?? "#"}>
      <article className="group cursor-pointer">
        <div className="overflow-hidden">
          <BlogImage image={image} title={title} />
        </div>
        <div className="space-y-3 py-6">
          <h3 className="line-clamp-2 text-xl font-bold leading-tight text-gray-900 transition-colors group-hover:text-gray-600">
            {title}
          </h3>
          <time className="text-sm text-gray-500" dateTime={publishedAt ?? ""}>
            {publishedAt
              ? new Date(publishedAt).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })
              : ""}
          </time>
        </div>
      </article>
    </Link>
  );
}

export function BlogHeader({
  title,
  description,
}: {
  title: string | null;
  description: string | null;
}) {
  return (
    <div className="relative mb-12 overflow-hidden">
      {/* Banner Image */}
      <div className="relative h-[400px] w-full md:h-[500px]">
        <Image
          src="/images/blog-banner.jpg"
          alt={"Blog header"}
          height={400}
          width={1920}
          priority
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-transparent" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="mx-auto max-w-4xl px-6 text-center">
            <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl md:text-6xl">
              {title}
            </h1>
            {description && (
              <p className="mx-auto mt-4 max-w-2xl text-lg text-white/90">
                {description}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
